<template>
  <div class="ux-multi-group" v-if="multiValue">
    <div v-if="showLabel" class="ux-group-title">
      <label class="ux-field-title" v-if="showLabel"
        >{{ plural || title }}
        <span class="ux-required-marker" v-if="required">*</span></label
      >
      <div class="ux-field-description" v-if="showDescription">
        {{ description }}
      </div>
    </div>
    <template v-if="modalEditor">
      <!-- Hidden validation forms — keep parity with inline mode so the parent form sees per-entry validity even without opening the modal -->
      <div class="ux-modal-validators" aria-hidden="true">
        <ux-form
          v-for="(element, index) in model"
          :key="`validator-${index}`"
          :trail="trail"
          :includeOfficeOnly="includeOfficeOnly"
          :submission="submission"
          :sandbox="sandbox"
          :ignoreDefaults="ignoreDefaults"
          :parentModel="parentModel"
          v-model="model[index]"
          :flex="sameLine"
          :fields="field.fields"
          @form:state="onEntryFormState(index, $event)"
        />
      </div>
      <draggable
        v-if="reorderable"
        v-model="model"
        :group="groupKey"
        @start="drag = true"
        @end="onReorderEnd"
        handle=".handle"
      >
        <template #item="{ element, index }">
          <div
            class="ux-modal-row"
            :class="{ 'has-error': isEntryInvalid(index) }"
            @click="openInModal(index)"
          >
            <flex-row vcenter gap>
              <flex-cell class="handle arrows" shrink @click.stop>
                <ux-icon icon="fa-arrows" />
              </flex-cell>
              <flex-cell>
                <strong>{{ multiLabel(element, index) }}</strong>

                <div
                  class="ux-modal-description"
                  v-if="element.description"
                  v-html="element.description"
                ></div>
                <div class="ux-modal-summary" v-if="entrySummary(element)">
                  {{ entrySummary(element) }}
                </div>
                <div>
                  <div v-if="isEntryInvalid(index)" class="ux-modal-row-error">
                    <ux-icon icon="fa-exclamation-triangle" left />
                    {{ entryErrorText(index) }}
                  </div>
                </div>
              </flex-cell>
              <template v-if="duplicatable || canRemoveValue">
                <flex-cell shrink>
                  <ux-button size="sm">
                    Edit
                    <ux-icon right icon="far fa-pen-to-square" />
                  </ux-button>
                </flex-cell>
                <flex-cell shrink v-if="duplicatable">
                  <ux-button
                    icon
                    size="sm"
                    tag="a"
                    @click.stop="duplicate(index)"
                  >
                    <ux-icon icon="far fa-copy" />
                  </ux-button>
                </flex-cell>

                <flex-cell shrink v-if="canRemoveValue">
                  <ux-button
                    icon
                    size="sm"
                    tag="a"
                    @click.stop="removeGroup(element, true)"
                  >
                    <ux-icon icon="fa-trash-alt" />
                  </ux-button>
                </flex-cell>
              </template>
              <template v-else>
                <flex-cell shrink>
                  <ux-button size="sm">
                    Edit
                    <ux-icon right icon="far fa-pen-to-square" />
                  </ux-button>
                </flex-cell>
              </template>
            </flex-row>
          </div>
        </template>
      </draggable>
      <template v-else>
        <div
          v-for="(element, index) in model"
          :key="`modal-row-${index}`"
          class="ux-modal-row"
          :class="{ 'has-error': isEntryInvalid(index) }"
          @click="openInModal(index)"
        >
          <flex-row vcenter gap>
            <flex-cell>
              <strong>{{ multiLabel(element, index) }}</strong>
              <div
                class="ux-modal-description"
                v-if="element.description"
                v-html="element.description"
              ></div>
              <div class="ux-modal-summary" v-if="entrySummary(element)">
                {{ entrySummary(element) }}
              </div>

              <div>
                <div v-if="isEntryInvalid(index)" class="ux-modal-row-error">
                  <ux-icon icon="fa-exclamation-triangle" left />
                  {{ entryErrorText(index) }}
                </div>
              </div>
            </flex-cell>
            <template v-if="duplicatable || canRemoveValue">
              <flex-cell shrink>
                <ux-button size="sm">
                  Edit
                  <ux-icon right icon="far fa-pen-to-square" />
                </ux-button>
              </flex-cell>
              <flex-cell shrink v-if="duplicatable">
                <ux-button
                  icon
                  size="sm"
                  tag="a"
                  @click.stop="duplicate(index)"
                >
                  <ux-icon icon="far fa-copy" />
                </ux-button>
              </flex-cell>
              <flex-cell shrink v-if="canRemoveValue">
                <ux-button
                  icon
                  size="sm"
                  tag="a"
                  @click.stop="removeGroup(element, true)"
                >
                  <ux-icon icon="fa-trash-alt" />
                </ux-button>
              </flex-cell>
            </template>
            <template v-else>
              <flex-cell shrink>
                <ux-button size="sm">
                  Edit
                  <ux-icon right icon="far fa-pen-to-square" />
                </ux-button>
              </flex-cell>
            </template>
          </flex-row>
        </div>
      </template>
    </template>
    <template v-else-if="reorderable">
      <!-- item-key="guid" -->
      <draggable
        v-model="model"
        :group="groupKey"
        @start="drag = true"
        @end="drag = false"
        handle=".handle"
      >
        <template #item="{ element, index }">
          <ux-panel ref="row">
            <!-- :key="guid" v-for="(element, index) in model" -->
            <ux-panel-header
              :class="{ 'last-child': collapsible && element.collapsed }"
            >
              <flex-row vcenter gap>
                <flex-cell class="handle arrows" shrink v-if="reorderable">
                  <ux-icon left icon="fa-arrows" />
                </flex-cell>
                <flex-cell vcenter>
                  <div>
                    <strong>{{ multiLabel(element, index) }}</strong>
                    <div
                      class="ux-modal-summary"
                      v-if="
                        collapsible &&
                        element.collapsed &&
                        entrySummary(element)
                      "
                    >
                      {{ entrySummary(element) }}
                    </div>
                  </div>
                </flex-cell>
                <flex-cell shrink v-if="duplicatable">
                  <ux-button icon size="xs" tag="a" @click="duplicate(index)">
                    <ux-icon icon="far fa-copy" />
                  </ux-button>
                </flex-cell>
                <flex-cell shrink v-if="collapsible">
                  <ux-button
                    size="xs"
                    tag="a"
                    @click="element.collapsed = !element.collapsed"
                  >
                    {{ element.collapsed ? "More" : "Less" }}
                    <ux-icon
                      right
                      :icon="
                        element.collapsed ? `fa-angle-down` : `fa-angle-up`
                      "
                    />
                  </ux-button>
                </flex-cell>
                <flex-cell vcenter shrink>
                  <ux-button
                    icon
                    tag="a"
                    v-if="canRemoveValue"
                    @click="remove(element)"
                  >
                    <ux-icon icon="fa-times" />
                  </ux-button>
                </flex-cell>
              </flex-row>
            </ux-panel-header>
            <ux-panel-body
              @keydown.enter="enterPress($event)"
              v-show="!collapsible || !element.collapsed"
            >
              <ux-form
                :trail="trail"
                ref="form"
                :includeOfficeOnly="includeOfficeOnly"
                :submission="submission"
                :sandbox="sandbox"
                :ignoreDefaults="ignoreDefaults"
                @form:state="stateChange"
                :parentModel="parentModel"
                v-model="model[index]"
                :flex="sameLine"
                :fields="field.fields"
              />
            </ux-panel-body>
          </ux-panel>
        </template>
      </draggable>
    </template>
    <template v-else>
      <!-- :key="guid"  -->
      <ux-panel ref="row" v-for="(element, index) in model">
        <ux-panel-header
          :class="{ 'last-child': collapsible && element.collapsed }"
        >
          <flex-row vcenter gap>
            <flex-cell shrink class="arrows handle" v-if="reorderable">
              <ux-icon icon="fa-arrows" />
            </flex-cell>
            <flex-cell vcenter>
              <div>
                <strong>{{ multiLabel(element, index) }}</strong>
                <div
                  class="ux-modal-summary"
                  v-if="
                    collapsible && element.collapsed && entrySummary(element)
                  "
                >
                  {{ entrySummary(element) }}
                </div>
              </div>
            </flex-cell>
            <flex-cell shrink v-if="duplicatable">
              <ux-button icon size="xs" tag="a" @click="duplicate(index)">
                <ux-icon icon="far fa-copy" />
              </ux-button>
            </flex-cell>
            <flex-cell shrink v-if="collapsible">
              <ux-button
                size="xs"
                tag="a"
                @click="element.collapsed = !element.collapsed"
              >
                {{ element.collapsed ? "More" : "Less" }}
                <ux-icon
                  right
                  :icon="element.collapsed ? `fa-angle-down` : `fa-angle-up`"
                />
              </ux-button>
            </flex-cell>
            <flex-cell vcenter shrink>
              <ux-button
                icon
                tag="a"
                v-if="canRemoveValue"
                @click="remove(element)"
              >
                <ux-icon icon="fa-times" />
              </ux-button>
            </flex-cell>
          </flex-row>
        </ux-panel-header>
        <ux-panel-body
          @keydown.enter="enterPress($event)"
          v-show="!element.collapsed"
        >
          <ux-form
            ref="form"
            :trail="trail"
            :includeOfficeOnly="includeOfficeOnly"
            :submission="submission"
            :sandbox="sandbox"
            :ignoreDefaults="ignoreDefaults"
            @form:state="stateChange"
            :parentModel="parentModel"
            v-model="model[index]"
            :flex="sameLine"
            :fields="field.fields"
          />
        </ux-panel-body>
      </ux-panel>
    </template>
    <ux-button v-if="canAddValue" @click="addGroup()"
      >{{ addLabel }}
      <ux-icon icon="fa-plus" right />
    </ux-button>
  </div>
  <template v-else>
    <template v-if="modalEditor">
      <!-- Hidden validator so the parent form sees the single object's validity -->
      <div class="ux-modal-validators" aria-hidden="true">
        <ux-form
          v-if="model"
          :trail="trail"
          :includeOfficeOnly="includeOfficeOnly"
          :submission="submission"
          :sandbox="sandbox"
          :ignoreDefaults="ignoreDefaults"
          :parentModel="parentModel"
          v-model="model"
          :flex="sameLine"
          :fields="field.fields"
          @form:state="onEntryFormState(0, $event)"
        />
      </div>

      <div :class="{ 'ux-single-group': showLabel }">
        <div v-if="showLabel" class="ux-group-title">
          <label class="ux-field-title"
            >{{ title }}
            <span class="ux-required-marker" v-if="required">*</span></label
          >
          <div class="ux-field-description" v-if="showDescription">
            {{ description }}
          </div>
        </div>

        <div
          v-if="model"
          class="ux-modal-row"
          :class="{ 'has-error': isEntryInvalid(0) }"
          @click="openInModal(0)"
        >
          <flex-row vcenter gap>
            <flex-cell>
              <strong>{{ multiLabel(model, 0) }}</strong>
              <div
                class="ux-modal-description"
                v-if="model.description"
                v-html="model.description"
              ></div>
              <div class="ux-modal-summary" v-if="entrySummary(model)">
                {{ entrySummary(model) }}
              </div>
              <div>
                <div v-if="isEntryInvalid(0)" class="ux-modal-row-error">
                  <ux-icon icon="fa-exclamation-triangle" left />
                  {{ entryErrorText(0) }}
                </div>
              </div>
            </flex-cell>
            <flex-cell shrink>
              <ux-button size="sm">
                Edit
                <ux-icon right icon="far fa-pen-to-square" />
              </ux-button>
            </flex-cell>
            <flex-cell shrink v-if="minimum === 0">
              <ux-button icon size="sm" tag="a" @click.stop="model = undefined">
                <ux-icon icon="fa-trash-alt" />
              </ux-button>
            </flex-cell>
          </flex-row>
        </div>
        <ux-button v-else @click="addAndOpenSingle()"
          >Add {{ label }}
          <ux-icon icon="fa-plus" right />
        </ux-button>
      </div>
    </template>
    <template v-else-if="widget === 'form'">
      <div class="ux-single-group" v-if="showLabel">
        <div class="ux-group-title">
          <label class="ux-field-title"
            >{{ title }}
            <span class="ux-required-marker" v-if="required">*</span></label
          >
          <div class="ux-field-description" v-if="showDescription">
            {{ description }}
          </div>
        </div>
        <ux-panel v-if="model">
          <ux-panel-header v-if="minimum === 0">
            <flex-row vcenter gap>
              <flex-cell vcenter />
              <flex-cell vcenter shrink>
                <ux-button icon tag="a" @click="model = undefined">
                  <ux-icon icon="fa-times" />
                </ux-button>
              </flex-cell>
            </flex-row>
          </ux-panel-header>
          <ux-panel-body>
            <ux-form
              ref="form"
              :trail="trail"
              :includeOfficeOnly="includeOfficeOnly"
              :submission="submission"
              :sandbox="sandbox"
              :ignoreDefaults="ignoreDefaults"
              @form:state="stateChange"
              :parentModel="parentModel"
              v-model="model"
              :flex="sameLine"
              :fields="field.fields"
            />
          </ux-panel-body>
        </ux-panel>
        <ux-button v-else @click="createSingleObject()"
          >Add {{ label }}
          <ux-icon icon="fa-plus" right />
        </ux-button>
      </div>
      <template v-else>
        <template v-if="model">
          <ux-panel v-if="minimum === 0">
            <ux-panel-header>
              <flex-row vcenter gap>
                <flex-cell vcenter>
                  <div>
                    <strong>{{ label }}</strong>
                  </div>
                </flex-cell>
                <flex-cell vcenter shrink>
                  <ux-button icon tag="a" @click="model = undefined">
                    <ux-icon icon="fa-times" />
                  </ux-button>
                </flex-cell>
              </flex-row>
            </ux-panel-header>
            <ux-panel-body>
              <ux-form
                ref="form"
                :trail="trail"
                :includeOfficeOnly="includeOfficeOnly"
                :submission="submission"
                :sandbox="sandbox"
                :ignoreDefaults="ignoreDefaults"
                @form:state="stateChange"
                :parentModel="parentModel"
                v-model="model"
                :flex="sameLine"
                :fields="field.fields"
              />
            </ux-panel-body>
          </ux-panel>
          <ux-form
            v-else
            ref="form"
            :trail="trail"
            :includeOfficeOnly="includeOfficeOnly"
            :submission="submission"
            :sandbox="sandbox"
            :ignoreDefaults="ignoreDefaults"
            @form:state="stateChange"
            :parentModel="parentModel"
            v-model="model"
            :flex="sameLine"
            :fields="field.fields"
          />
        </template>
        <ux-button v-else @click="createSingleObject()"
          >Add {{ label }}
          <ux-icon icon="fa-plus" right />
        </ux-button>
      </template>
    </template>
    <template v-else>
      <div class="ux-single-group" v-if="showLabel">
        <div class="ux-group-title">
          <label class="ux-field-title" v-if="showLabel">{{ title }}</label>
          <div class="ux-field-description" v-if="showDescription">
            {{ description }}
          </div>
        </div>
        <ux-panel>
          <ux-panel-body>
            <ux-form
              ref="form"
              :trail="trail"
              :includeOfficeOnly="includeOfficeOnly"
              :submission="submission"
              :sandbox="sandbox"
              :ignoreDefaults="ignoreDefaults"
              @form:state="stateChange"
              :parentModel="parentModel"
              v-model="model"
              :flex="sameLine"
              :fields="field.fields"
            />
          </ux-panel-body>
        </ux-panel>
      </div>
      <template v-else>
        <ux-form
          ref="form"
          :trail="trail"
          :includeOfficeOnly="includeOfficeOnly"
          :submission="submission"
          :sandbox="sandbox"
          :ignoreDefaults="ignoreDefaults"
          @form:state="stateChange"
          :parentModel="parentModel"
          v-model="model"
          :flex="sameLine"
          :fields="field.fields"
        />
      </template>
    </template>
  </template>
</template>
<script>
import InputMixin from "./input-mixin";
// import draggable from 'vuedraggable'
import draggable from "vuedraggable/src/vuedraggable";
import GroupEditModal from "../../modal/GroupEditModal.vue";

export default {
  mixins: [InputMixin],
  //TODO check whether we should move draggable into the input mixin
  components: { draggable },
  props: {
    paymentConfiguration: {
      type: Object,
    },
    sandbox: {
      type: Boolean,
    },
    submission: {
      type: Boolean,
      default: false,
    },
    ignoreDefaults: {
      type: Boolean,
      default: false,
    },
    includeOfficeOnly: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Object, Array],
    },
    trail: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      drag: false,
      entryStates: {},
    };
  },
  created() {
    // if (Array.isArray(this.modelValue)) {
    //     this.modelValue.forEach(function(entry) {
    //         entry.guid = Math.random();
    //     })
    // }

    // Persisted records can contain null entries in multi-value groups
    // (sparse writes predating the cleanInput null-strip). cleanInput only
    // runs on the write path, so normalise the initial value before first
    // paint or the row render crashes on entry.description
    if (Array.isArray(this.value)) {
      const cleaned = this.value.filter((entry) => entry != null);
      if (cleaned.length !== this.value.length) {
        this.value = cleaned;
      }
    }
  },
  computed: {
    groupKey() {
      return `${this.field.key}-order`;
    },
    duplicatable() {
      return this.field.duplicatable;
    },
    collapsible() {
      return this.field.collapsible;
    },
    showLabel() {
      return this.field.showLabel;
    },
    reorderable() {
      return this.multiValue && this.field.reorderable;
    },
    modalEditor() {
      return this.field.modal === true;
    },
    sameLine() {
      return this.field.sameLine;
    },
    aggregateModalState() {
      const states = Object.values(this.entryStates || {});
      let invalid = false;
      const invalidFields = [];
      states.forEach(function (s) {
        if (!s) return;
        if (s.invalid) invalid = true;
        if (Array.isArray(s.invalidFields)) {
          invalidFields.push.apply(invalidFields, s.invalidFields);
        }
      });
      return {
        valid: !invalid,
        invalid: invalid,
        invalidFields: invalidFields,
      };
    },
  },
  watch: {
    aggregateModalState: {
      deep: true,
      handler(state) {
        if (this.modalEditor) {
          this.$emit("form:state", state);
        }
      },
    },
  },
  emits: ["form:state"],
  methods: {
    createSingleObject() {
      this.model = {};
    },
    stateChange(details) {
      this.$emit("form:state", details);
    },
    // multiDescription(entry, index) {
    //   return entry.description;
    // },
    multiLabel(entry, index) {
      if (!entry) {
        return `${this.label} ${index + 1}`;
      }

      if (entry.title) {
        return entry.title;
      }

      if (entry.name) {
        return entry.name;
      }

      if (entry.firstName) {
        if (entry.lastName) {
          return `${entry.firstName} ${entry.lastName}`;
        }
        return entry.firstName;
      }

      return `${this.label} ${index + 1}`;
    },
    // Realtime one-line summary for a collapsed modal row. Provide a
    // `summary` function on the field config (or field.expressions.summary)
    // — it receives { model, field, parentModel } and returns a string.
    entrySummary(element) {
      const summarise = this.field?.summary || this.field?.expressions?.summary;
      if (typeof summarise !== "function") return "";
      try {
        const result = summarise({
          model: element,
          field: this.field,
          parentModel: this.parentModel,
        });
        return typeof result === "string" ? result : "";
      } catch (err) {
        return "";
      }
    },
    enterPress(event) {
      return;
      if (this.canAddValue) {
        event.stopImmediatePropagation();
        event.preventDefault();
        this.add();
      }
    },
    refocus() {
      const self = this;

      self.$nextTick(function () {
        //Find the last row
        var rows = self.$refs.row || [];

        var lastRow = rows[rows.length - 1];

        if (!lastRow) {
          return;
        }
        var input = lastRow.$el.querySelector(".ux-field-focus");

        if (!input) {
          return;
        }
        input.focus();
      });
    },
    getNewDefaultEntry() {
      return {
        // guid: Math.random(),
      };
    },
    duplicate(index) {
      let element = this.model[index];
      if (!element) {
        return;
      }

      const copy = JSON.parse(JSON.stringify(element));
      this.model.splice(index + 1, 0, copy);
      this.entryStates = {};
    },
    addGroup() {
      this.add();
      if (this.modalEditor) {
        const self = this;
        this.$nextTick(function () {
          self.openInModal(self.value.length - 1);
        });
      }
    },
    async removeGroup(element, warn) {
      if (warn) {
        const title =
          element.title ||
          element.name ||
          element.firstName ||
          `this ${this.field.title}`;

        await this.$sdk.confirm(`Are you sure you want to delete ${title}`);
      }
      this.remove(element);
      this.entryStates = {};
    },
    onEntryFormState(index, state) {
      this.entryStates = { ...this.entryStates, [index]: state };
    },
    onReorderEnd() {
      this.drag = false;
      this.entryStates = {};
    },
    isEntryInvalid(index) {
      return !!this.entryStates[index]?.invalid;
    },
    entryErrorText(index) {
      const state = this.entryStates[index];
      if (!state?.invalid) return "";
      const fields = state.invalidFields || [];
      if (!fields.length) return "Some fields need attention";
      const first = fields[0];
      return (
        first.message ||
        `${first.title || first.key || "A field"} needs attention`
      );
    },
    async openInModal(index) {
      const self = this;
      const isMulti = self.multiValue;
      const element = isMulti ? self.model[index] : self.model;
      const labelIndex = index == null ? 0 : index;

      const options = {
        eyebrow: self.label,
        title: self.multiLabel(element, labelIndex),
        fields: self.field.fields,
        sameLine: self.sameLine,
      };

      if (isMulti) {
        options.array = self.model;
        options.index = index;
      } else {
        options.model = self.model;
        options.onUpdate = function (value) {
          self.model = value;
        };
      }

      await self.$sdk
        .modal({ component: GroupEditModal, options })
        .catch(function () {});
    },
    addAndOpenSingle() {
      this.createSingleObject();
      const self = this;
      this.$nextTick(function () {
        self.openInModal(0);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.ux-multi-group,
.ux-single-group {
  margin: 2.5em 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.ux-group-title {
  font-size: 1.2em;
  margin-bottom: 1em;
  margin-top: 1em;
}

.arrows {
  cursor: pointer;
  .ux-icon {
    opacity: 0.3;
  }
}

:deep(.panel) {
  margin-bottom: 0.5em;
}

.ux-modal-validators {
  display: none;
}

.ux-modal-row {
  @media (prefers-color-scheme: light) {
    background: var(--bg1);
    border: 1px solid color-mix(in lab, currentColor 15%, var(--bg1));
  }

  @media (prefers-color-scheme: dark) {
    background: var(--bg1);
    border: 1px solid color-mix(in lab, currentColor 18%, var(--bg1));
  }

  border-radius: 0.5em;

  /* background: color-mix(in lab, currentColor 2%, var(--bg2)); */
  padding: 0.8em 1em;
  margin-bottom: 0.5em;
  cursor: pointer;

  &:hover {
    border-color: color-mix(in lab, currentColor 22%, var(--bg1));
    background: color-mix(in lab, currentColor 4%, var(--bg1));
  }

  &.has-error {
    border-color: color-mix(in lab, var(--red, #df7d7d) 50%, var(--bg1));
    background: color-mix(in lab, var(--red, #df7d7d) 6%, var(--bg1));

    &:hover {
      border-color: color-mix(in lab, var(--red, #df7d7d) 70%, var(--bg1));
      background: color-mix(in lab, var(--red, #df7d7d) 9%, var(--bg1));
    }
  }

  .ux-modal-row-error {
    margin-top: 4px;
    font-size: 0.82em;
    color: var(--red, #df7d7d);
    font-weight: 500;
    opacity: 0.9;
  }
}

.ux-modal-summary {
  margin-top: 2px;
  font-size: 0.82em;
  font-weight: 400;
  opacity: 0.6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 48em;
}
</style>
